<template>
  <aside>

    <div class="modals">
      <!-- Delete Bot Modal Window -->
      <ModalWindow @modalClickOnBackground="setDeleteBotModalState(false)" :modalOpen="botDeleteModal" class="delete-bot-modal">
        <div class="title">Delete bot</div>
        <p class="text">
          Are you sure you want to delete <span class="highlight">"{{bot.botName}}"</span> bot?
        </p>
        <div class="actions">
          <BotActionButton class="red shadow" @click.prevent="confirmBotDeletion()">Delete</BotActionButton>
          <BotActionButton class="gray shadow" @click="setDeleteBotModalState(false)">Cancel</BotActionButton>
        </div>
      </ModalWindow>
    </div>


    <div class="top-content">
      <div class="section bot-actions">
        <div class="sub-section buttons" v-if="bot.canEdit">
          <BotActionButton class="gray shadow" @click="$root.$emit('addDialogue')">Add Dialogue</BotActionButton>
          <BotActionButton class="blue shadow" @click="saveBot()" :class="{ disabled: botSaveButtonActive }">Save Bot</BotActionButton>
        </div>
        <div class="sub-section buttons" v-if="!bot.canEdit">
          Read only
        </div>
      </div>

      <div class="section bot-info">
        <div class="sub-section">
          <h3>Send out-of-dialogue messages to QCRM</h3> 
          <div class="section-content">
            <BasicSwitch v-model="bot.qcrmDirect" />
          </div>
        </div>
      </div>

      <div class="section bot-info">
        <div class="sub-section">
          <h3>Bot Name</h3>
          <div class="section-content">
            <BasicInput v-model="bot.botName" placeholder="Bot Name" class="name-input" @focus="deleteError" />
            <div v-if="botNameError.length > 0" class="input-error">{{botNameError}}</div>
          </div>
        </div>

        <div class="sub-section">
          <h3>Company</h3>
          <div class="section-content">
            <select name="companies" v-model="bot.companyId" class="basic-select">
              <option v-for="company in editableCompanies" :key="company.id" :value="company.id">{{company.name}}</option>
            </select>
          </div>
        </div>

      </div>
    </div>

    <div class="bottom-content">
      <div class="sub-section">
        <BotActionButton class="red shadow" @click="setDeleteBotModalState(true)">Delete Bot</BotActionButton>
      </div>
    </div>

  </aside>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BotActionButton from "@/components/buttons/BotActionButton";
import BasicInput from "@/components/inputs/BasicInput";
import BasicSwitch from "@/components/buttons/BasicSwitch";
import ModalWindow from "@/components/app/general/ModalWindow";
import helpers from "@/helpers";
import validators from "@/validators";

export default {
  data: () => {
    return {
      botDeleteModal: false,
      botNameError: "",
      botIsSaving: false
    };
  },
  computed: {
    ...mapGetters(["bot", "editableCompanies"]),
    botSaveButtonActive: function() {
      return this.botIsSaving;
    }
  },
  async mounted() {
    this.botDeleteIntention = false;

    this.$root.$on("botSavingDone", () => {
      setTimeout(() => {
        this.setBotSavingStatus(false);
      }, 500);
    });
  },
  methods: {
    ...mapActions(["deleteBot", "saveBotAction"]),
    setDeleteBotModalState(value) {
      this.botDeleteModal = value;
    },
    async saveBot() {
      if (!this.botSaveButtonActive) {
        this.setBotSavingStatus(true);
        this.bot.botName = this.bot.botName.trim();
        const {valid, error} = validators.isBotNameValid(this.bot.botName);
        if (valid) {
          const data = {
            id: this.bot.id,
            name: this.bot.botName,
            qcrmDirect: this.bot.qcrmDirect,
            companyId: this.bot.companyId
          };
          await this.saveBotAction(data);
          setTimeout(() => {
            this.setBotSavingStatus(false);
          }, 500);
        } else {
          this.botNameError = error;
        }
      }
    },
    async confirmBotDeletion() {
      const result = await this.deleteBot(this.bot.id);
      if (result){
        return this.$router.push("/");
      }
    },
    setBotSavingStatus(value) {
      this.botIsSaving = value;
    },
    deleteError() {
      this.botNameError = "";
    }
  },
  components: { BotActionButton, BasicInput, BasicSwitch, ModalWindow }
};
</script>

<style lang="scss">
$textColorLightGray: rgba(
  $color: #fff,
  $alpha: 0.85
);

aside {
  width: 320px;
  min-height: 380px;
  height: calc(100vh - 60px);
  float: right;
  box-sizing: border-box;
  border-left: 1px solid #555555;
  color: #a1a1a1;
  background-color: #393939;
  overflow: hidden;
}

.top-content {
  width: 100%;
  height: calc(100vh - 128px);
  min-height: 320px;
  padding: 0 0 0 18px;
}
.bottom-content {
  width: 100%;
  height: 68px;
  border-top: 1px solid #555555;
  display: flex;
  justify-content: center;
  align-items: center;
}

.section {
  width: 100%;
  border-bottom: 1px solid #555555;
  padding: 10px 18px 10px 0;

  &:last-child {
    border-bottom: none;
  }

  .sub-section {
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
    }

    .bot-action:first-child {
      margin-left: 0;
    }

    &.buttons {
      display: flex;
      justify-content: space-between;

      .bot-action:last-child {
        margin-right: 0;
      }
    }
    h1, h2, h3, h4, h5 {
      color: $textColorLightGray;
      font-weight: 500;
      margin: 10px 0;
    }
    h1 {
      font-size: 18px;
    }
    h2 {
      font-size: 16px;
    }
    h3 {
      font-size: 14px;
    }

    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
}
.name-input {
  width: 80%;
}
.input-error {
  margin-top: 8px;
  font-size: 12px;
  color: #ff0707;
}

.basic-select {
  width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  background-color: #4D4D4D;
  color: #fff;
  padding: 6px 9px;
  outline: none;
  border: 1px solid #686868;
  max-width: 100%;
}
</style>